import { BehaviorSubject, Observable, Subject } from 'rxjs';
import { Inject, Injectable } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import { UserInterface } from '@sport-activities/sdk';

import { APP_STORAGE } from '~/app.token';
import { Activity } from '~/core/model/activity.model';
import { CheckoutItem } from '~/checkout/model/checkout-item.model';
import { CheckoutItemInterface } from '~/checkout/interfaces/checkout-item.interface';
import { RedirectionService } from '~/core/services/redirection/redirection.service';
import { Subscriber } from '~/checkout/interfaces/subscriber.interface';
import { Timeslot } from '~/core/model/timeslot.model';
import { ToggleNameEnum } from '~/toggles/services/toggle/enums/name.enum';
import { ToggleService } from '~/toggles/services/toggle.service';
import { UserError } from '~/errors/class/error/user-error';
import { BmsTermsInterface } from '~/checkout/components/pages/payment/onepay/payment-onepay.component';
import { joinWithCommasAnd } from '~/core/helpers/array/array.helper';

const CART_STORAGE_KEY = 'checkout.cart';
const MULTI_CART_STORAGE_KEY = 'multi.checkout.cart';

@Injectable({
  providedIn: 'root',
})
export class CheckoutService {
  private readonly bookingDelay: number;

  public readonly toggleNameEnum = ToggleNameEnum;

  public contactPhoneNumberData: Pick<
    UserInterface,
    'phoneNumber' | 'keepPhoneNumber'
  >;

  // current activities in cart
  public cartItems: CheckoutItem[] = [];
  public items: CheckoutItem[] = [];

  // load storage checkout by default
  public cartItemsSubject = new BehaviorSubject<CheckoutItem[]>(
    this.getCartContent()
  );
  public itemsSubject = new BehaviorSubject<CheckoutItem[]>(
    this.loadCheckout()
  );
  public newItemSubject = new Subject<CheckoutItem>();

  constructor(
    @Inject(APP_STORAGE) private readonly storage: Storage,
    private readonly redirectionService: RedirectionService,
    private readonly translateService: TranslateService,
    readonly toggleService: ToggleService
  ) {
    this.bookingDelay =
      +toggleService.getVariant(ToggleNameEnum.BookingDelay, '15') || 15;
  }

  public get externalOrigin(): boolean {
    return !!this.redirectUrl;
  }

  // Previous Url
  public get previousRedirectUrl(): string | null {
    return this.storage.getItem('checkout.previousRedirectUrl');
  }

  public set previousRedirectUrl(url: string) {
    this.storage.setItem('checkout.previousRedirectUrl', url);
  }

  public get redirectUrl(): string | null {
    return this.storage.getItem('checkout.redirectUrl');
  }

  public set redirectUrl(url: string | null) {
    if (url === null) {
      this.storage.removeItem('checkout.redirectUrl');
      return;
    }

    this.storage.setItem('checkout.redirectUrl', url);
  }

  // trim an array of object with name and surname key
  private formatAttendeeItem(subscribers: Subscriber[]): Subscriber[] {
    return subscribers.map((subscriber) => {
      return {
        ...subscriber,
        firstname: subscriber.firstname.trim(),
        lastname: subscriber.lastname.trim(),
      };
    });
  }

  /**
   * Checkout saving
   */

  /** retrieve checkout and cart from session storage  */
  private loadCheckout(): CheckoutItem[] {
    const itemsString = this.storage.getItem(CART_STORAGE_KEY);

    if (!itemsString) {
      return;
    }

    const items: CheckoutItemInterface[] = JSON.parse(itemsString);

    this.items = items
      .map((item) => {
        item.activity = new Activity(item.activity);
        item.timeslot = new Timeslot(item.timeslot);

        return new CheckoutItem(item);
      })
      .filter((checkoutItem) => {
        if (
          checkoutItem.timeslot.isBookable(
            checkoutItem.activity?.bookableUntilTheEnd,
            checkoutItem.numberOfAttendees,
            this.bookingDelay
          )
        ) {
          return checkoutItem;
        }
      });

    return this.items;
  }

  // add item to cart and notify subscribers
  public addCheckoutItem(item: CheckoutItemInterface): void {
    const newItem = new CheckoutItem(item);

    if (
      !item.timeslot.isBookable(
        newItem.activity?.bookableUntilTheEnd,
        newItem.numberOfAttendees,
        this.bookingDelay
      )
    ) {
      throw new UserError(
        this.translateService.instant(
          'checkout.error.subscription.unavailable.description'
        ),
        this.translateService.instant(
          'checkout.error.subscription.unavailable.title'
        )
      );
    }

    // if timeslot already in cart, update information
    if (this.hasItem(newItem)) {
      this.updateCheckoutItem(newItem);
      return;
    }

    this.items = [newItem]; // push only one item in cart
    this.itemsSubject.next(this.items);
    this.newItemSubject.next(newItem);

    this.saveCheckout();
    this.setCartItem(newItem);
  }

  // get subject as observable
  public getItems(): Observable<CheckoutItem[]> {
    return this.itemsSubject.asObservable();
  }

  public getCartItems(): Observable<CheckoutItem[]> {
    return this.cartItemsSubject.asObservable();
  }

  // timeslot is already in checkout
  public hasItem(item: CheckoutItem): boolean {
    return this.items.some(
      (checkoutItem) =>
        checkoutItem.timeslot.identifier === item.timeslot.identifier
    );
  }

  // timeslot is already in cart
  public cartHasItem(item: Timeslot): boolean {
    return this.cartItems.some(
      (checkoutItem) => checkoutItem.timeslot.identifier === item.identifier
    );
  }

  public getBmsTermsTrans(
    allBmsTerms: BmsTermsInterface[],
    consent: boolean
  ): string {
    const bmsTerms = allBmsTerms?.filter((term) => !!term) ?? [];
    if (bmsTerms.length === 0) {
      return '';
    }

    const translationKey = 'checkout.summary.conditions.bms.terms'
    const termTransKey = consent ? `${translationKey}.consent` : translationKey;

    const andTrans = this.translateService.instant('timeslot.and');
    const termTrans = this.translateService.instant(termTransKey);
    const partnerTrans = this.translateService.instant(translationKey + '.partner');

    const termsMap = new Map<string, BmsTermsInterface[]>();
    for (const t of bmsTerms) {
      const name = t.providerName;
      if (!termsMap.has(name)) {
        termsMap.set(name, []);
      }
      termsMap.get(name).push(t);
    }

    const termsArray = [];
    for (const [providerName, terms] of termsMap) {
      const termLinks = terms.map(
        (d) => `<a href="${d.url}">${d.name}</a>`
      );
      termsArray.push(`${joinWithCommasAnd(termLinks)} ${partnerTrans} ${providerName}`);
    }

    return termTrans + ' ' + joinWithCommasAnd(termsArray, andTrans);
  }

  // is checkout empty
  public hasItems(): boolean {
    return this.items.length !== 0;
  }

  // is free checkout
  public isFreeCheckout(): boolean {
    return !this.cartItems.some((item) => item.isFree === false);
  }

  // get notified when new item added to cart
  public newItem(): Observable<CheckoutItem> {
    return this.newItemSubject.asObservable();
  }

  /**
   * redirect the user to confirm page
   * or the redirectUrl if it exists
   */
  public redirectToConfirmation(bookingItemId?: string): Promise<boolean> {
    // if redirect url is set, redirect to it
    const redirectUrl = this.redirectUrl;
    if (redirectUrl) {
      this.redirectUrl = null;
      window.location.href = redirectUrl;
      return Promise.resolve(true);
    }

    return this.redirectionService.redirect('/checkout.confirmation.free', {
      queryParams: { bookingItemId },
    });
  }

  /** reset cart storage */
  public resetCheckout(): void {
    this.storage.removeItem(CART_STORAGE_KEY);
  }

  /** save to storage */
  public saveCheckout(): void {
    this.storage.setItem(CART_STORAGE_KEY, JSON.stringify(this.items));
  }

  // set current items
  public setCheckoutItem(item: CheckoutItemInterface): void {
    this.items = [];
    this.addCheckoutItem({
      ...item,
      ...{
        adults: this.formatAttendeeItem(item.adults),
        children: this.formatAttendeeItem(item.children),
        bmsTerms: item?.bmsTerms,
      },
    });
  }

  public updateCheckoutItem(newItem: CheckoutItem): void {
    const existingIndex = this.items
      .map((item) => item.timeslot.identifier)
      .indexOf(newItem.timeslot.identifier);

    if (existingIndex < 0) {
      return;
    }

    this.items[existingIndex] = Object.assign(
      this.items[existingIndex],
      newItem
    );

    // notify
    this.itemsSubject.next(this.items);
    this.newItemSubject.next(this.items[existingIndex]);
    this.saveCheckout();
  }

  public getCartContent(): CheckoutItem[] {
    const itemsString = this.storage.getItem(MULTI_CART_STORAGE_KEY);

    if (!itemsString) {
      return;
    }

    const items: CheckoutItem[] = JSON.parse(itemsString);

    this.cartItems = items
      .map((item) => {
        item.activity = new Activity(item.activity);
        item.timeslot = new Timeslot(item.timeslot);

        return new CheckoutItem(item);
      })
      .filter((checkoutItem) => {
        if (
          checkoutItem.timeslot.isBookable(
            checkoutItem.activity?.bookableUntilTheEnd,
            checkoutItem.numberOfAttendees,
            this.bookingDelay
          )
        ) {
          return checkoutItem;
        }
      });
    this.storage.setItem(
      MULTI_CART_STORAGE_KEY,
      JSON.stringify(this.cartItems)
    );
    return this.cartItems;
  }

  public setCartItem(item: CheckoutItem): void {
    const itemsString = this.storage.getItem(MULTI_CART_STORAGE_KEY);
    let items = [];

    if (itemsString) {
      items = JSON.parse(itemsString);
    }

    items.push(item);

    const itemsFiltered = items
      .filter((item) => {
        return item.adults.length > 0 || item.children.length > 0;
      })
      .sort((a, b) => {
        return (
          new Date(a.timeslot.startDate).getTime() -
          new Date(b.timeslot.startDate).getTime()
        );
      });

    this.cartItemsSubject.next(itemsFiltered);
    this.storage.setItem(MULTI_CART_STORAGE_KEY, JSON.stringify(itemsFiltered));
  }

  public deleteCartItem(item: Timeslot): void {
    const index = this.cartItems.findIndex(
      (o) => o.timeslot.identifier === item.identifier
    );
    if (index > -1) {
      this.cartItems.splice(index, 1);
      this.cartItemsSubject.next(this.cartItems);
      this.storage.setItem(
        MULTI_CART_STORAGE_KEY,
        JSON.stringify(this.cartItems)
      );
    }
  }

  public emptyCart(): void {
    this.cartItemsSubject.next([]);
    this.cartItems = [];
    this.storage.removeItem(MULTI_CART_STORAGE_KEY);
  }
}
